import { useLayoutEffect, useMemo, useRef, useState } from "react";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import CrosswordClueList from "./CrosswordClueList";
import CrosswordGrid from "./CrosswordGrid";
import {
  cluesSection,
  containerStyles,
  PAGE_CONTENT_HEIGHT,
} from "./CrosswordPuzzle.styles";

import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import type {
  CrosswordClue,
  CrosswordPuzzleFormData,
} from "@/types/models/Exportables.interface";

const isCrosswordPuzzleData = (data: any): data is CrosswordPuzzleFormData => {
  return (
    data &&
    typeof data === "object" &&
    ("grid" in data || "across_clues" in data || "down_clues" in data)
  );
};

/**
 * Builds a map from grid "x,y" positions to clue numbers,
 * used to render small numbers in the top-left of grid cells.
 * Across clues are numbered 1..N, down clues continue N+1..M.
 */
const buildPositionNumbers = (
  acrossClues: CrosswordClue[],
  downClues: CrosswordClue[],
) => {
  const positionToNumber = new Map<string, number>();

  acrossClues.forEach((clue, idx) => {
    const key = `${clue.start_x},${clue.start_y}`;
    if (!positionToNumber.has(key)) {
      positionToNumber.set(key, idx + 1);
    }
  });

  downClues.forEach((clue, idx) => {
    const key = `${clue.start_x},${clue.start_y}`;
    if (!positionToNumber.has(key)) {
      positionToNumber.set(key, acrossClues.length + idx + 1);
    }
  });

  return positionToNumber;
};

const CrosswordPuzzle = ({
  formData,
  onChange,
  title = "Vocab Crossword Puzzle:",
  isStudent = false,
}: TemplateProps<CrosswordPuzzleFormData>) => {
  const data = isCrosswordPuzzleData(formData)
    ? formData
    : { grid: [], across_clues: [], down_clues: [] };

  const articleTitle = data.article_title || data.articleShortTitle || "";
  const grid = data.grid ?? [];
  const acrossClues = data.across_clues ?? [];
  const downClues = data.down_clues ?? [];
  const hintLetters = data.hint_letters ?? {};
  const studentAnswers = data.student_answers ?? {};

  const positionToNumber = useMemo(
    () => buildPositionNumbers(acrossClues, downClues),
    [acrossClues, downClues],
  );

  const cluesSectionRef = useRef<HTMLDivElement>(null);
  const [maxGridHeight, setMaxGridHeight] = useState<number | undefined>();

  useLayoutEffect(() => {
    if (!cluesSectionRef.current) return;
    const cluesHeight = cluesSectionRef.current.offsetHeight;
    // Header (~60px) + cluesSection margin (48px) + grid padding (16px) + container padding (32px)
    const overhead = 60 + 48 + 16 + 32;
    const remaining = PAGE_CONTENT_HEIGHT - cluesHeight - overhead;
    setMaxGridHeight(Math.max(remaining, 200));
  }, [acrossClues, downClues]);

  const handleChange = (update: Partial<CrosswordPuzzleFormData>) => {
    if (!onChange) return;
    onChange(update);
  };

  const updateCellLetter = (x: number, y: number, value: string) => {
    const letter = value.slice(-1).toUpperCase();
    const key = `${x},${y}`;
    if (isStudent) {
      handleChange({
        student_answers: { ...studentAnswers, [key]: letter },
      });
    } else {
      handleChange({
        hint_letters: { ...hintLetters, [key]: letter },
      });
    }
  };

  const updateClue = (
    direction: "across_clues" | "down_clues",
    idx: number,
    value: string,
  ) => {
    const clues = direction === "across_clues" ? acrossClues : downClues;
    const updated = clues.map((clue, i) =>
      i === idx ? { ...clue, clue: value } : clue,
    );
    handleChange({ [direction]: updated });
  };

  return (
    <div css={containerStyles}>
      <ExportablesTemplateHeaderSection
        templateTitle={`${title}\n${articleTitle}`}
      />

      <div ref={cluesSectionRef} css={cluesSection}>
        <CrosswordClueList
          title="Across"
          clues={acrossClues}
          direction="across_clues"
          startNumber={1}
          isStudent={isStudent}
          onClueChange={updateClue}
        />
        <CrosswordClueList
          title="Down"
          clues={downClues}
          direction="down_clues"
          startNumber={acrossClues.length + 1}
          isStudent={isStudent}
          onClueChange={updateClue}
        />
      </div>

      <CrosswordGrid
        grid={grid}
        positionToNumber={positionToNumber}
        hintLetters={hintLetters}
        studentAnswers={studentAnswers}
        isStudent={isStudent}
        onCellChange={updateCellLetter}
        maxGridHeight={maxGridHeight}
      />
    </div>
  );
};

export default CrosswordPuzzle;
